// ExamResponseService.js
// Servicio para manejar el guardado de respuestas de exámenes/evaluaciones

import axios from 'axios';
import { ENDPOINTS as LMS_ENDPOINTS } from './Endpoints';

/**
 * Guarda un intento completo de examen con todas sus respuestas
 * @param {Object} params - Parámetros del intento
 * @param {number} params.idExamen - ID del examen
 * @param {number} params.idEstudiante - ID del estudiante
 * @param {Array} params.preguntas - Array de preguntas del examen
 * @param {Object} params.respuestasUsuario - Objeto con las respuestas del usuario
 * @param {Object} params.resultado - Objeto con los resultados calculados
 * @returns {Promise<Object>} - Resultado del guardado
 */
export const saveExamAttempt = async ({
    idExamen,
    idEstudiante,
    preguntas,
    respuestasUsuario,
    resultado
}) => {
    try {
        console.log('[ExamResponseService] Iniciando guardado de intento de examen...');
        console.log('Parámetros:', { idExamen, idEstudiante, totalPreguntas: preguntas.length });

        // Paso 1: Crear el intento de examen
        const calificacion = resultado.total > 0
            ? (resultado.correctas / resultado.total) * 100
            : 0;

        const aprobado = calificacion >= 60; // REGLA: 60% aprueba

        const intentoData = {
            IdExamenIntento: 0, // Nuevo intento
            IdExamen: parseInt(idExamen, 10),
            IdEstudiante: parseInt(idEstudiante, 10),
            FechaIntento: new Date().toISOString(),
            Resultado: aprobado,
            Calificacion: calificacion,
            Activo: true
        };

        console.log('[ExamResponseService] Creando intento:', intentoData);
        const resIntento = await axios.post(LMS_ENDPOINTS.examenesIntentos, intentoData);

        // El backend puede retornar el ID directamente o en data
        const idExamenIntento = typeof resIntento.data === 'number'
            ? resIntento.data
            : resIntento.data?.idExamenIntento || resIntento.data?.IdExamenIntento;

        if (!idExamenIntento) {
            throw new Error('No se pudo obtener el ID del intento creado');
        }

        console.log('[ExamResponseService] Intento creado con ID:', idExamenIntento);

        // Paso 2: Guardar cada respuesta individual
        const respuestasGuardadas = [];
        const errores = [];

        for (const pregunta of preguntas) {
            try {
                const idPregunta = pregunta.idPregunta;

                // Determinar el tipo de pregunta y obtener la respuesta
                const tipoRespuesta = determinarTipoRespuesta(pregunta, respuestasUsuario, idPregunta);

                if (!tipoRespuesta.contestada) {
                    console.log(`[ExamResponseService] Pregunta ${idPregunta} no contestada, omitiendo...`);
                    continue;
                }

                // Guardar la respuesta
                const resultadoRespuesta = await saveIndividualResponse({
                    idExamenIntento,
                    pregunta,
                    tipoRespuesta,
                    idPregunta
                });

                respuestasGuardadas.push(resultadoRespuesta);
                console.log(`[ExamResponseService] Respuesta guardada para pregunta ${idPregunta}`);

            } catch (error) {
                console.error(`[ExamResponseService] Error guardando respuesta para pregunta ${pregunta.idPregunta}:`, error);
                errores.push({
                    idPregunta: pregunta.idPregunta,
                    error: error.message
                });
            }
        }

        console.log('[ExamResponseService] Guardado completado');
        console.log(`Total respuestas guardadas: ${respuestasGuardadas.length}`);
        console.log(`Total errores: ${errores.length}`);

        return {
            success: true,
            idExamenIntento,
            respuestasGuardadas: respuestasGuardadas.length,
            errores: errores.length > 0 ? errores : null,
            calificacion,
            aprobado
        };

    } catch (error) {
        console.error('[ExamResponseService] Error general al guardar intento:', error);
        throw error;
    }
};

/**
 * Determina el tipo de respuesta y si fue contestada
 */
const determinarTipoRespuesta = (pregunta, respuestasUsuario, idPregunta) => {
    // Verificar si es pregunta con espacios en blanco (texto libre)
    const descripcion = pregunta.descripcionPregunta || pregunta.DescripcionPregunta || pregunta.descripcion || '';
    const tieneEspaciosBlanco = descripcion.includes('{}');

    if (tieneEspaciosBlanco) {
        // Buscar respuestas de espacios en blanco
        const respuestasBlanco = [];
        let idx = 0;
        while (respuestasUsuario[`${idPregunta}_blank_${idx}`] !== undefined) {
            respuestasBlanco.push(respuestasUsuario[`${idPregunta}_blank_${idx}`]);
            idx++;
        }

        if (respuestasBlanco.length > 0) {
            // Verificar si es correcta
            const esCorrecta = verificarRespuestaTextoLibre(pregunta, respuestasBlanco);

            return {
                tipo: 'texto_libre',
                contestada: true,
                respuesta: respuestasBlanco.join(' | '), // Concatenar respuestas
                esCorrecta
            };
        }
    }

    // Verificar si es verdadero/falso
    if (pregunta.tipo === 'verdaderoFalso' || (pregunta.idTipoPregunta && pregunta.idTipoPregunta === 3)) {
        const respuestaVF = respuestasUsuario[idPregunta];
        if (respuestaVF !== undefined && respuestaVF !== null) {
            const respuestaCorrecta = pregunta.respuestaVerdaderoFalso?.respuestaCorrecta;
            const esCorrecta = String(respuestaVF) === String(respuestaCorrecta);

            return {
                tipo: 'verdadero_falso',
                contestada: true,
                respuesta: respuestaVF, // boolean string ("true"/"false")
                esCorrecta
            };
        }
    }

    // Verificar si es opción múltiple
    const seleccion = respuestasUsuario[idPregunta];
    if (seleccion) {
        const opciones = pregunta.opciones || [];

        if (Array.isArray(seleccion)) {
            // Múltiples opciones seleccionadas
            const idsSeleccionados = seleccion.map(String);
            const idsCorrectos = opciones
                .filter(o => o.esCorrecta || o.EsCorrecta)
                .map(o => String(o.idOpcion || o.IdOpcion));

            // Calificación estricta
            const esCorrecta = idsSeleccionados.length === idsCorrectos.length &&
                idsSeleccionados.every(id => idsCorrectos.includes(id));

            return {
                tipo: 'opcion_multiple_multiple',
                contestada: true,
                idOpciones: idsSeleccionados,
                esCorrecta
            };
        } else {
            // Opción única (simple)
            const idOpcionSeleccionada = seleccion;
            const opcionSeleccionada = opciones.find(
                o => String(o.idOpcion || o.IdOpcion) === String(idOpcionSeleccionada)
            );

            if (opcionSeleccionada) {
                const esCorrecta = opcionSeleccionada.esCorrecta !== undefined
                    ? opcionSeleccionada.esCorrecta
                    : opcionSeleccionada.EsCorrecta !== undefined
                        ? opcionSeleccionada.EsCorrecta
                        : false;

                return {
                    tipo: 'opcion_multiple',
                    contestada: true,
                    idOpcion: idOpcionSeleccionada,
                    esCorrecta
                };
            }
        }
    }

    return {
        tipo: 'desconocido',
        contestada: false
    };
};

/**
 * Verifica si una respuesta de texto libre es correcta
 */
const verificarRespuestaTextoLibre = (pregunta, respuestasUsuario) => {
    console.log('=== VERIFICANDO RESPUESTA TEXTO LIBRE ===');
    console.log('Pregunta completa:', pregunta);
    console.log('Respuestas del usuario:', respuestasUsuario);

    // Obtener las respuestas correctas
    let respuestasCorrectas = [];

    // Opción 1: Buscar en respuestaTextoLibre
    if (pregunta.respuestaTextoLibre) {
        console.log('Tiene respuestaTextoLibre:', pregunta.respuestaTextoLibre);
        const palabrasClave = pregunta.respuestaTextoLibre.palabrasClave ||
            pregunta.respuestaTextoLibre.PalabrasClave || '';
        if (palabrasClave) {
            respuestasCorrectas = [palabrasClave];
            console.log('Palabras clave encontradas:', palabrasClave);
        }
    }

    // Opción 2: Si no hay respuestas de texto libre, buscar en opciones correctas
    if (respuestasCorrectas.length === 0 && pregunta.opciones) {
        console.log('Buscando en opciones, total opciones:', pregunta.opciones.length);
        const opcionesCorrectas = pregunta.opciones.filter(o => {
            const esCorrecta = o.esCorrecta || o.EsCorrecta || o.correcta;
            console.log(`Opción: "${o.descripcion || o.Descripcion || o.texto || o.Texto}", esCorrecta: ${esCorrecta}`);
            return esCorrecta;
        });

        respuestasCorrectas = opcionesCorrectas.map(o => o.descripcion || o.Descripcion || o.texto || o.Texto || '');
        console.log('Respuestas correctas de opciones:', respuestasCorrectas);
    }

    if (respuestasCorrectas.length === 0) {
        console.warn('⚠️ No se encontraron respuestas correctas definidas para esta pregunta');
        // Por defecto, marcar como correcta si no hay definición
        // Esto evita penalizar al estudiante por errores de configuración
        return true;
    }

    console.log('Respuestas correctas finales:', respuestasCorrectas);
    console.log('Respuestas del usuario a verificar:', respuestasUsuario);

    // Comparar (case-insensitive y trimmed)
    for (let i = 0; i < respuestasUsuario.length; i++) {
        const respuestaUsuario = (respuestasUsuario[i] || '').trim().toLowerCase();
        const respuestaCorrecta = (respuestasCorrectas[i] || respuestasCorrectas[0] || '').trim().toLowerCase();

        console.log(`Comparando [${i}]: "${respuestaUsuario}" === "${respuestaCorrecta}"`);

        if (respuestaUsuario !== respuestaCorrecta) {
            console.log(`❌ NO COINCIDE en posición ${i}`);
            return false;
        } else {
            console.log(`✓ COINCIDE en posición ${i}`);
        }
    }

    console.log('✅ TODAS LAS RESPUESTAS SON CORRECTAS');
    return true;
};

/**
 * Guarda una respuesta individual
 */
const saveIndividualResponse = async ({
    idExamenIntento,
    pregunta,
    tipoRespuesta,
    idPregunta
}) => {
    console.log('=== GUARDANDO RESPUESTA INDIVIDUAL (EXAM) ===');
    console.log('idExamenIntento:', idExamenIntento);
    console.log('idPregunta:', idPregunta);
    console.log('tipoRespuesta:', tipoRespuesta);

    // Paso 1: Crear el registro en ExamenesRespuestas
    const respuestaData = {
        IdExamenRespuesta: 0,
        IdExamenIntento: idExamenIntento,
        IdPregunta: idPregunta,
        Correcta: tipoRespuesta.esCorrecta,
        Explicacion: '', // Opcional, se puede agregar después
        Activo: true
    };

    console.log('📤 POST a ExamenesRespuestas:', respuestaData);
    console.log('Endpoint:', LMS_ENDPOINTS.examenesRespuestas);

    const resRespuesta = await axios.post(LMS_ENDPOINTS.examenesRespuestas, respuestaData);

    console.log('📥 Respuesta del servidor (ExamenesRespuestas):', resRespuesta.data);

    const idExamenRespuesta = typeof resRespuesta.data === 'number'
        ? resRespuesta.data
        : resRespuesta.data?.idExamenRespuesta || resRespuesta.data?.IdExamenRespuesta;

    if (!idExamenRespuesta) {
        console.error('❌ No se pudo obtener el ID de la respuesta creada');
        console.error('Respuesta completa del servidor:', resRespuesta);
        throw new Error('No se pudo obtener el ID de la respuesta creada');
    }

    console.log('✅ ID de respuesta obtenido:', idExamenRespuesta);

    // Paso 2: Guardar el detalle según el tipo de respuesta
    if (tipoRespuesta.tipo === 'texto_libre') {
        const textoLibreData = {
            IdExamenRespuestaTextoLibre: 0,
            IdExamenRespuesta: idExamenRespuesta,
            Respuesta: tipoRespuesta.respuesta,
            Activo: true
        };

        console.log('📤 POST a RespuestaExamenTextoLibre:', textoLibreData);
        console.log('Endpoint:', LMS_ENDPOINTS.respuestaExamenTextoLibre);

        const resTextoLibre = await axios.post(LMS_ENDPOINTS.respuestaExamenTextoLibre, textoLibreData);

        console.log('📥 Respuesta del servidor (TextoLibre):', resTextoLibre.data);

    } else if (tipoRespuesta.tipo === 'opcion_multiple') {
        const opcionMultipleData = {
            IdExamenOpcionMultiple_Det: 0,
            IdExamenRespuesta: idExamenRespuesta,
            IdOpcion: parseInt(tipoRespuesta.idOpcion, 10),
            Activo: true
        };

        console.log('📤 POST a RespuestaExamenOpcionMultipleDet:', opcionMultipleData);
        console.log('Endpoint:', LMS_ENDPOINTS.respuestaExamenOpcionMultipleDet);

        const resOpcionMultiple = await axios.post(LMS_ENDPOINTS.respuestaExamenOpcionMultipleDet, opcionMultipleData);

        console.log('📥 Respuesta del servidor (OpcionMultiple):', resOpcionMultiple.data);

    } else if (tipoRespuesta.tipo === 'opcion_multiple_multiple') {
        // Guardar múltiples detalles para examen
        const ids = tipoRespuesta.idOpciones || [];
        console.log(`📤 Guardando ${ids.length} opciones múltiples (Examen)...`);

        for (const idOpcion of ids) {
            const opcionMultipleData = {
                IdExamenOpcionMultiple_Det: 0,
                IdExamenRespuesta: idExamenRespuesta,
                IdOpcion: parseInt(idOpcion, 10),
                Activo: true
            };
            await axios.post(LMS_ENDPOINTS.respuestaExamenOpcionMultipleDet, opcionMultipleData);
        }

    } else if (tipoRespuesta.tipo === 'verdadero_falso') {
        const verdaderoFalsoData = {
            IdExamenRespuestaVerdaderoFalso: 0,
            IdExamenRespuesta: idExamenRespuesta,
            Respuesta: Boolean(String(tipoRespuesta.respuesta) === 'true'),
            Activo: true
        };

        console.log('📤 POST a RespuestaExamenVerdaderoFalso:', verdaderoFalsoData);
        console.log('Endpoint:', LMS_ENDPOINTS.respuestaExamenVerdaderoFalso);

        const resVerdaderoFalso = await axios.post(LMS_ENDPOINTS.respuestaExamenVerdaderoFalso, verdaderoFalsoData);
        console.log('📥 Respuesta del servidor (VerdaderoFalso):', resVerdaderoFalso.data);
    }

    console.log('✅ Respuesta individual guardada completamente');

    return {
        idExamenRespuesta,
        idPregunta,
        tipo: tipoRespuesta.tipo,
        correcta: tipoRespuesta.esCorrecta
    };
};

/**
 * Obtiene el historial de intentos de un estudiante para un examen
 */
export const getExamAttempts = async (idExamen, idEstudiante) => {
    try {
        const response = await axios.get(LMS_ENDPOINTS.examenesIntentos);
        const todosIntentos = response.data || [];

        const intentosDelEstudiante = todosIntentos.filter(
            intento =>
                (intento.idExamen || intento.IdExamen) === parseInt(idExamen, 10) &&
                (intento.idEstudiante || intento.IdEstudiante) === parseInt(idEstudiante, 10)
        );

        return intentosDelEstudiante.sort((a, b) => {
            const fechaA = new Date(a.fechaIntento || a.FechaIntento);
            const fechaB = new Date(b.fechaIntento || b.FechaIntento);
            return fechaB - fechaA; // Más reciente primero
        });

    } catch (error) {
        console.error('[ExamResponseService] Error obteniendo intentos:', error);
        return [];
    }
};

/**
 * Obtiene las respuestas de un intento específico
 */
export const getAttemptResponses = async (idExamenIntento) => {
    try {
        const [resRespuestas, resTextoLibre, resOpcionMultiple, resVerdaderoFalso] = await Promise.all([
            axios.get(LMS_ENDPOINTS.examenesRespuestas),
            axios.get(LMS_ENDPOINTS.respuestaExamenTextoLibre),
            axios.get(LMS_ENDPOINTS.respuestaExamenOpcionMultipleDet),
            axios.get(LMS_ENDPOINTS.respuestaExamenVerdaderoFalso || '').catch(() => ({ data: [] }))
        ]);

        const todasRespuestas = resRespuestas.data || [];
        const respuestasDelIntento = todasRespuestas.filter(
            r => (r.idExamenIntento || r.IdExamenIntento) === parseInt(idExamenIntento, 10)
        );

        // Enriquecer con detalles
        const respuestasEnriquecidas = respuestasDelIntento.map(respuesta => {
            const idRespuesta = respuesta.idExamenRespuesta || respuesta.IdExamenRespuesta;

            // Buscar detalle de texto libre
            const textoLibre = (resTextoLibre.data || []).find(
                tl => (tl.idExamenRespuesta || tl.IdExamenRespuesta) === idRespuesta
            );

            // Buscar detalle de opción múltiple
            const opcionMultiple = (resOpcionMultiple.data || []).find(
                om => (om.idExamenRespuesta || om.IdExamenRespuesta) === idRespuesta
            );

            // Buscar detalle de verdadero falso
            const verdaderoFalso = (resVerdaderoFalso.data || []).find(
                vf => (vf.idExamenRespuesta || vf.IdExamenRespuesta) === idRespuesta
            );

            return {
                ...respuesta,
                textoLibre,
                opcionMultiple,
                verdaderoFalso
            };
        });

        return respuestasEnriquecidas;

    } catch (error) {
        console.error('[ExamResponseService] Error obteniendo respuestas del intento:', error);
        return [];
    }
};
